
import { Component, EventEmitter, Input, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { SubscriptionHandlerDirective } from '../../directives/subscription-handler.directive';
import { Message, MessageMetadata } from '../../models/chat.models';

import { ChatService } from '../../services/chat.service';
import { FeedbackState, UserSentiment } from '../../enums/chat.enum';
import { tap } from 'rxjs/operators';

@Component({
    selector: 'app-chat-feedback',
    templateUrl: './chat-feedback.component.html',
    styleUrls: ['./chat-feedback.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ChatFeedbackComponent extends SubscriptionHandlerDirective implements OnInit {
    @Input() isMobileApp: boolean = false;
    @Input() message: Message;
    @Output() feedbackState = new EventEmitter<FeedbackState>();

    constructor(private chatService: ChatService) {
        super();
    }
    
    activeFormStep: FeedbackState;
    showFeedbackForm: boolean = false;
    loading: boolean = false;

    userFeedback: string = "";

    get userSentiment() {
        return this.message._metadata?.userSentiment ?? UserSentiment.NEUTRAL;
    }

    set userSentiment(sentiment: UserSentiment) {
        this.message._metadata!.userSentiment = sentiment;
    }

    ngOnInit(): void {
        this.setFeedbackState(FeedbackState.SUBMIT_SENTIMENT);
    }

    /**
     * Toggle feedback buttons. 
     * If the user likes/dislikes a response, display the option to provide written feedback
     * User can undo a like/dislike
     */
    toggleLike() {
        this.updateSentiment(UserSentiment.LIKE);
    }

    toggleDislike() {
        this.updateSentiment(UserSentiment.DISLIKE);
    }

    private updateSentiment(toSentiment: UserSentiment) {
        if (this.userSentiment != toSentiment) {
            this.userSentiment = toSentiment;
            this.setFeedbackState(FeedbackState.CONFIRM_FEEDBACK);
        } else {
            this.userSentiment = UserSentiment.NEUTRAL;
            this.resetUserSentiment();
        }
    }

    onEnableFeedback() {
        this.setFeedbackState(FeedbackState.SUBMIT_FEEDBACK);
    }

    /**
     * Submits feedback for this message.
     * @param withFeedback If true, submit user's written feedback. Else, only submit the sentiment (like/dislike)
     */
    onSubmit(withFeedback: boolean) {
        this.loading = true;
        let feedback = withFeedback ? this.userFeedback : null;
        this.chatService.submitMessageFeedback(this.message._metadata!.messageId, this.userSentiment, feedback).pipe(
            tap(() => {
                this.userFeedback = "";
                (this.message._metadata as MessageMetadata).userSentiment = this.userSentiment;
                this.setFeedbackState(FeedbackState.CANCEL);
                this.loading = false;
            })
        ).subscribe();
    }

    /**
     * Resets user's feedback for this message
     */
    resetUserSentiment() {
        this.loading = true;
        this.chatService.resetMessageFeedback(this.message._metadata!.messageId).pipe(
            tap(() => {
                this.setFeedbackState(FeedbackState.CANCEL);
                this.loading = false;
            })
        ).subscribe();
    }

    private setFeedbackState(state: FeedbackState) {
        this.activeFormStep = state;
        this.message._metadata!.feedbackState = state;
        this.feedbackState.emit(state);
    }
}
